import { useState, useEffect, useCallback } from 'react';
import api from '../api';
import { useConfirm } from "./ConfirmDialog";

async function safe(fn) { try { return await fn(); } catch { return null; } }

function Alert({ msg, onClose }) {
  if (!msg) return null;
  const cls = { success:'nv-alert-ok', error:'nv-alert-err', warn:'nv-alert-warn' }[msg.type] || 'nv-alert-ok';
  return (
    <div className={`nv-alert ${cls}`} style={{ marginBottom:14 }}>
      <span style={{ flex:1 }}>{msg.text}</span>
      <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer', color:'inherit', fontSize:14 }}>✕</button>
    </div>
  );
}

/* ── Modal Agregar Rango ── */
function ModalRango({ range, onClose, onSave }) {
  const isEdit = !!range?.id;
  const [form, setForm] = useState(range ? {
    provincia: range.provincia||'', cod_provincia: range.cod_provincia||'', codigo_area: range.codigo_area||'',
    serie_inicio: range.serie_inicio||'', serie_fin: range.serie_fin||'', cantidad_total: range.cantidad_total||'',
    resolucion_arcotel: range.resolucion_arcotel||'', fecha_resolucion: range.fecha_resolucion ? String(range.fecha_resolucion).slice(0,10) : '',
  } : { provincia:'', cod_provincia:'', codigo_area:'', serie_inicio:'', serie_fin:'', cantidad_total:'', resolucion_arcotel:'', fecha_resolucion:'' });
  const [busy, setBusy] = useState(false);
  const [err,  setErr]  = useState(null);
  const set = (k,v) => setForm(f => ({...f,[k]:v}));

  // El total de DIDs SIEMPRE deriva de la serie (fin - inicio + 1). Bug previo: al editar,
  // el campo pre-llenado pisaba el auto-cálculo (parseInt(form.cantidad_total) || ...) →
  // cambiar la serie no actualizaba el conteo. Ahora se recalcula al cambiar inicio/fin
  // (corrige también datos viejos al re-guardar).
  useEffect(() => {
    const ini = parseInt(form.serie_inicio, 10), fin = parseInt(form.serie_fin, 10);
    if (Number.isFinite(ini) && Number.isFinite(fin) && fin >= ini) {
      const n = String(fin - ini + 1);
      setForm(f => (f.cantidad_total === n ? f : { ...f, cantidad_total: n }));
    }
  }, [form.serie_inicio, form.serie_fin]);

  const submit = async () => {
    if (!form.provincia || !form.serie_inicio || !form.serie_fin) return setErr('Provincia, serie inicio y fin son requeridos');
    setBusy(true); setErr(null);
    try {
      const payload = { ...form, cantidad_total: parseInt(form.cantidad_total) || (parseInt(form.serie_fin) - parseInt(form.serie_inicio) + 1) };
      if (isEdit) await api.put(`/did-ranges/${range.id}`, payload);
      else        await api.post('/did-ranges', payload);
      onSave();
    } catch(e) { setErr(e?.response?.data?.detail || (isEdit ? 'Error al editar rango' : 'Error al crear rango')); }
    finally { setBusy(false); }
  };

  return (
    <div className="nv-modal-overlay" onClick={e => e.target===e.currentTarget && onClose()}>
      <div className="nv-modal" style={{ maxWidth:580 }}>
        <div className="nv-modal-header">
          <span className="nv-modal-title">{isEdit ? 'Editar rango DID' : 'Agregar rango DID (ARCOTEL)'}</span>
          <button className="nv-modal-close" onClick={onClose}>✕</button>
        </div>
        {err && <div className="nv-alert nv-alert-err" style={{ marginBottom:14 }}>{err}</div>}

        <div className="nv-form-row">
          <div className="nv-form-field">
            <label className="nv-label">Provincia *</label>
            <input className="nv-input" placeholder="ej: PICHINCHA" value={form.provincia}
              onChange={e => set('provincia',e.target.value.toUpperCase())} />
          </div>
          <div className="nv-form-field">
            <label className="nv-label">Cód. provincia</label>
            <input className="nv-input" placeholder="ej: 17" value={form.cod_provincia}
              onChange={e => set('cod_provincia',e.target.value)} />
          </div>
          <div className="nv-form-field">
            <label className="nv-label">Código de área</label>
            <input className="nv-input" placeholder="ej: 2" value={form.codigo_area}
              onChange={e => set('codigo_area',e.target.value)} />
          </div>
        </div>
        <div className="nv-form-row">
          <div className="nv-form-field">
            <label className="nv-label">Serie inicio *</label>
            <input className="nv-input" placeholder="ej: 2000000" value={form.serie_inicio}
              onChange={e => set('serie_inicio',e.target.value)}
              style={{ fontFamily:'var(--font-mono)' }} />
          </div>
          <div className="nv-form-field">
            <label className="nv-label">Serie fin *</label>
            <input className="nv-input" placeholder="ej: 2999999" value={form.serie_fin}
              onChange={e => set('serie_fin',e.target.value)}
              style={{ fontFamily:'var(--font-mono)' }} />
          </div>
          <div className="nv-form-field">
            <label className="nv-label">Cantidad total</label>
            <input className="nv-input" placeholder="Auto-calculado" value={form.cantidad_total} readOnly
              title="Derivado de la serie (fin − inicio + 1)"
              style={{ background:'var(--bg-raised)', color:'var(--text-muted)', cursor:'not-allowed' }} />
          </div>
        </div>
        <div className="nv-form-row">
          <div className="nv-form-field">
            <label className="nv-label">Resolución ARCOTEL</label>
            <input className="nv-input" placeholder="ej: ARCOTEL-2024-001" value={form.resolucion_arcotel}
              onChange={e => set('resolucion_arcotel',e.target.value)} />
          </div>
          <div className="nv-form-field">
            <label className="nv-label">Fecha resolución</label>
            <input className="nv-input" type="date" value={form.fecha_resolucion}
              onChange={e => set('fecha_resolucion',e.target.value)} />
          </div>
        </div>

        <div className="nv-modal-footer">
          <button className="nv-btn nv-btn-ghost" onClick={onClose}>Cancelar</button>
          <button className="nv-btn nv-btn-primary" onClick={submit} disabled={busy}>
            {busy ? <span className="nv-spinner" /> : (isEdit ? '✓ Guardar cambios' : '+ Agregar rango')}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Componente principal ── */
export default function DIDSeries() {
  const [ranges,    setRanges]    = useState([]);
  const [clientes,  setClientes]  = useState([]);
  const [didsAsig,  setDidsAsig]  = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [tab,       setTab]       = useState('rangos');
  const [search,    setSearch]    = useState('');
  const [modal,     setModal]     = useState(null);
  const [editRange, setEditRange] = useState(null);
  const [msg,       setMsg]       = useState(null);
  // window.confirm no abre cartel en el navegador y devuelve false: cancelaba solo.
  const [confirmar, dialogoConfirm] = useConfirm();

  const showMsg = (text, type='success') => { setMsg({text,type}); setTimeout(()=>setMsg(null),4000); };

  const load = useCallback(async () => {
    setLoading(true);
    const [r,c,d] = await Promise.all([
      safe(() => api.get('/did-ranges')),
      safe(() => api.get('/clientes?limit=500')),
      safe(() => api.get('/dids/asignados')),
    ]);
    setRanges(r?.data?.data || []);
    setClientes(c?.data?.data || []);
    setDidsAsig(d?.data?.data || []);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);


  const deleteRange = async (r) => {
    if (!(await confirmar(`¿Eliminar el rango ${r.provincia} (${r.serie_inicio}–${r.serie_fin})?`))) return;
    try {
      await api.delete(`/did-ranges/${r.id}`);
      showMsg('Rango eliminado');
      load();
    } catch(e) { showMsg(e?.response?.data?.detail || 'Error al eliminar', 'error'); }
  };

  // Fuente de verdad = la MISMA unión que usa el allocator (did_engine.assign_did): el DID
  // de cada LÍNEA (client_lines.did_number, lo que aprovisiona add_line) ∪ clients.did_number
  // (legacy / self-service). El endpoint /dids/asignados la resuelve. Antes esto derivaba
  // SOLO de clients.did_number y perdía todo DID provisionado por línea (add_line no lo toca)
  // → /dids mostraba "sin asignados" aunque el cliente sí tuviera su DID en la pestaña Líneas.
  const didAsignadosReales = didsAsig
    .filter(a => a.did_completo != null && String(a.did_completo).trim() !== '')
    .map(a => ({
      id:             a.did_completo,
      did_completo:   String(a.did_completo).trim(),
      cliente_id:     a.cliente_id,
      cliente_nombre: a.cliente_nombre || '—',
      estado:         'asignado',
    }));

  // KPIs — derivados de la verdad, NO del contador paralelo did_ranges.cantidad_usada
  const totalDIDs      = ranges.reduce((s,r) => s + (r.cantidad_total||0), 0);
  const usados         = didAsignadosReales.length;                 // DIDs efectivamente en clientes
  const disponibles    = Math.max(0, totalDIDs - usados);
  const ocupacion      = totalDIDs > 0 ? ((usados/totalDIDs)*100).toFixed(1) : 0;
  const clientesConDID = new Set(didAsignadosReales.map(a=>a.cliente_id)).size;

  // Usados por rango = clientes cuyo did_number cae dentro del rango (MISMA fuente de verdad
  // que los KPI). El contador almacenado did_ranges.cantidad_usada es legacy y se desfasa
  // cuando un DID cambia por la ficha del cliente (no toca ese contador).
  const usadosPorRango = (r) => {
    const area = String(r.codigo_area ?? '');
    const ini = Number(r.serie_inicio), fin = Number(r.serie_fin);
    return didAsignadosReales.filter(a => {
      const d = String(a.did_completo || '');
      if (!d.startsWith('593' + area)) return false;
      const serie = Number(d.slice(3 + area.length));
      return Number.isFinite(serie) && serie >= ini && serie <= fin;
    }).length;
  };

  // Filtro asignados (sobre la fuente de verdad)
  const filtAsig = didAsignadosReales.filter(a => {
    const q = search.toLowerCase();
    return !q || a.did_completo?.includes(q) || a.cliente_nombre?.toLowerCase().includes(q);
  });

  return (
    <div>
      {dialogoConfirm}
      {/* Header */}
      <div className="nv-page-header">
        <div>
          <div className="nv-page-title">DID / Numeración</div>
          <div className="nv-page-sub">Gestión de rangos ARCOTEL y asignación de números</div>
        </div>
        <div className="nv-page-actions">
          <button className="nv-btn nv-btn-secondary nv-btn-sm" onClick={load} disabled={loading}>
            {loading ? <span className="nv-spinner" /> : '↺'} Actualizar
          </button>
          <button className="nv-btn nv-btn-ghost" onClick={() => setModal('rango')}>
            + Rango ARCOTEL
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div className="nv-kpi-grid" style={{ marginBottom:16 }}>
        <div className="nv-kpi">
          <div className="nv-kpi-label">Total DIDs</div>
          <div className="nv-kpi-value" style={{ color:'var(--brand)' }}>{totalDIDs.toLocaleString()}</div>
          <div className="nv-kpi-sub">{ranges.length} rangos</div>
        </div>
        <div className="nv-kpi">
          <div className="nv-kpi-label">Asignados</div>
          <div className="nv-kpi-value" style={{ color:'var(--warning)' }}>{usados.toLocaleString()}</div>
          <div className="nv-kpi-sub">{ocupacion}% ocupación</div>
        </div>
        <div className="nv-kpi">
          <div className="nv-kpi-label">Disponibles</div>
          <div className="nv-kpi-value" style={{ color:'var(--success)' }}>{disponibles.toLocaleString()}</div>
          <div className="nv-kpi-sub">Libres para asignar</div>
        </div>
        <div className="nv-kpi">
          <div className="nv-kpi-label">Clientes con DID</div>
          <div className="nv-kpi-value" style={{ color:'var(--info)' }}>
            {clientesConDID}
          </div>
          <div className="nv-kpi-sub">De {clientes.length} totales</div>
        </div>
      </div>

      <Alert msg={msg} onClose={() => setMsg(null)} />

      {/* Tabs */}
      <div className="nv-tabs">
        {[['rangos','Rangos ARCOTEL'],['asignados','DIDs asignados']].map(([k,l]) => (
          <div key={k} className={`nv-tab${tab===k?' active':''}`} onClick={() => setTab(k)}>{l}</div>
        ))}
      </div>

      {/* ── Tab Rangos ── */}
      {tab === 'rangos' && (
        <div className="nv-card" style={{ padding:0 }}>
          {loading ? (
            <div className="nv-loading"><span className="nv-spinner" /></div>
          ) : ranges.length === 0 ? (
            <div style={{ textAlign:'center', padding:'48px 20px' }}>
              <div style={{ fontSize:32, opacity:.2, marginBottom:12 }}>◉</div>
              <div style={{ fontSize:13, color:'var(--text-muted)', marginBottom:16 }}>Sin rangos DID registrados</div>
              <button className="nv-btn nv-btn-primary" onClick={() => setModal('rango')}>+ Agregar rango ARCOTEL</button>
            </div>
          ) : (
            <div className="nv-table-wrap">
              <table className="nv-table">
                <thead><tr>
                  <th>Provincia</th><th>Área</th><th>Serie inicio</th><th>Serie fin</th>
                  <th>Total</th><th>Usados</th><th>Disponibles</th><th>Ocupación</th><th>Resolución</th><th></th>
                </tr></thead>
                <tbody>
                  {ranges.map(r => {
                    const usados = usadosPorRango(r);
                    const disp = (r.cantidad_total||0) - usados;
                    const pct  = r.cantidad_total > 0 ? ((usados/r.cantidad_total)*100).toFixed(0) : 0;
                    return (
                      <tr key={r.id}>
                        <td style={{ fontWeight:500, color:'var(--text-primary)' }}>{r.provincia}</td>
                        <td className="mono">{r.codigo_area}</td>
                        <td className="mono">{r.serie_inicio}</td>
                        <td className="mono">{r.serie_fin}</td>
                        <td className="mono">{(r.cantidad_total||0).toLocaleString()}</td>
                        <td className="mono" style={{ color:'var(--warning)' }}>{usados.toLocaleString()}</td>
                        <td className="mono" style={{ color:'var(--success)' }}>{disp.toLocaleString()}</td>
                        <td>
                          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                            <div className="nv-progress" style={{ flex:1, minWidth:60 }}>
                              <div className="nv-progress-bar" style={{
                                width:`${pct}%`,
                                background: pct > 80 ? 'var(--danger)' : pct > 50 ? 'var(--warning)' : 'var(--success)'
                              }} />
                            </div>
                            <span style={{ fontSize:10, fontFamily:'var(--font-mono)', color:'var(--text-muted)', minWidth:28 }}>{pct}%</span>
                          </div>
                        </td>
                        <td style={{ fontSize:10, color:'var(--text-muted)' }}>{r.resolucion_arcotel || '—'}</td>
                        <td style={{ whiteSpace:'nowrap', textAlign:'right' }}>
                          <button className="nv-btn nv-btn-ghost nv-btn-sm" onClick={() => setEditRange(r)} title="Editar rango">✎</button>
                          <button className="nv-btn nv-btn-danger nv-btn-sm" onClick={() => deleteRange(r)} title="Eliminar rango" style={{ marginLeft:6 }}>✕</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Tab Asignados ── */}
      {tab === 'asignados' && (
        <div className="nv-card" style={{ padding:0 }}>
          <div style={{ padding:'10px 14px', borderBottom:'1px solid var(--border)' }}>
            <div className="nv-search">
              <span style={{ color:'var(--text-muted)', fontSize:12 }}>⌕</span>
              <input placeholder="Buscar por número o cliente..."
                value={search} onChange={e => setSearch(e.target.value)} />
              {search && <button onClick={() => setSearch('')}
                style={{ background:'none', border:'none', color:'var(--text-muted)', cursor:'pointer' }}>✕</button>}
            </div>
          </div>
          {loading ? (
            <div className="nv-loading"><span className="nv-spinner" /></div>
          ) : filtAsig.length === 0 ? (
            <div style={{ textAlign:'center', padding:'48px 20px' }}>
              <div style={{ fontSize:32, opacity:.2, marginBottom:12 }}>◉</div>
              <div style={{ fontSize:13, color:'var(--text-muted)', marginBottom:16 }}>
                {search ? 'Sin resultados' : 'Sin DIDs asignados aún'}
              </div>
              {!search && <div style={{ fontSize:11, color:'var(--text-muted)' }}>El DID se asigna desde la ficha del cliente en <strong>Clientes</strong>.</div>}
            </div>
          ) : (
            <div className="nv-table-wrap">
              <table className="nv-table">
                <thead><tr>
                  <th>DID</th><th>Cliente</th><th>Trunk</th><th>Provincia</th><th>Estado</th><th>Asignado</th><th></th>
                </tr></thead>
                <tbody>
                  {filtAsig.map(a => (
                    <tr key={a.id}>
                      <td className="mono" style={{ color:'var(--brand)', fontWeight:600 }}>{a.did_completo}</td>
                      <td style={{ color:'var(--text-primary)', fontWeight:500 }}>{a.cliente_nombre || '—'}</td>
                      <td style={{ fontSize:11, color:'var(--text-muted)' }}>{a.trunk_nombre || '—'}</td>
                      <td style={{ fontSize:11, color:'var(--text-muted)' }}>{a.provincia || '—'}</td>
                      <td>
                        <span className={`nv-badge ${a.estado==='asignado'?'nv-badge-ok':'nv-badge-muted'}`}>
                          <span className="dot" />{a.estado}
                        </span>
                      </td>
                      <td style={{ fontSize:10, color:'var(--text-muted)' }}>
                        {a.fecha_asignacion ? new Date(a.fecha_asignacion).toLocaleDateString('es-EC',{day:'2-digit',month:'short',year:'numeric'}) : '—'}
                      </td>
                      <td style={{ fontSize:10, color:'var(--text-muted)' }}
                          title="El DID es el clients.did_number del cliente; se cambia/libera desde la ficha del cliente en Clientes">
                        gestionar en Clientes
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={{ padding:'8px 14px', borderTop:'1px solid var(--border)', fontSize:10, color:'var(--text-muted)', textAlign:'center' }}>
                {filtAsig.length} DIDs asignados
              </div>
            </div>
          )}
        </div>
      )}

      {modal === 'rango' && (
        <ModalRango
          onClose={() => setModal(null)}
          onSave={() => { setModal(null); load(); showMsg('Rango ARCOTEL agregado'); }} />
      )}
      {editRange && (
        <ModalRango range={editRange}
          onClose={() => setEditRange(null)}
          onSave={() => { setEditRange(null); load(); showMsg('Rango actualizado'); }} />
      )}
    </div>
  );
}
